// A stored assignment decision, in the shape the scoring board already reads.
//
// The board and its row builder (settings/auto-assign/scoreBoardRows.js) were
// written for the simulator, whose verdicts are nested: `{ driverId, eligible,
// reason, scored: { score, terms, facts } }`. The backend flattens the same
// information before storing it — `packCandidates` in util/assignment-log.js
// keeps the reasoning and throws away the Sequelize instances — so this puts the
// nesting back rather than teaching the board a second shape.
//
// One shape, one board, one explanation of a decision. A second renderer would
// drift from the first within a release.
import { rowsFromVerdicts } from '../../../../../settings/auto-assign/scoreBoardRows.js';
import { resolveScoring } from '../../../../../settings/auto-assign/autoAssignScoring.js';
import { skipReasonText } from '../../../../../settings/auto-assign/autoAssignLabels.js';

const toVerdict = candidate => ({
	driverId: candidate.deliveryId,
	eligible: candidate.eligible,
	reason: candidate.reason,
	scored: candidate.eligible
		? {
				score: candidate.score ?? 0,
				terms: candidate.terms || [],
				facts: {
					idleMinutes: candidate.idleMinutes ?? null,
					liveOrders: candidate.liveOrders ?? 0,
					meters: candidate.meters ?? null,
					rawMeters: candidate.rawMeters ?? null,
					routeMode: candidate.routeMode ?? null,
					positionTrust: candidate.positionTrust ?? 1,
					supplyScarcity: candidate.supplyScarcity ?? null,
					freeRiders: candidate.freeRiders ?? null,
					waitingOrders: candidate.waitingOrders ?? null,
				},
			}
		: null,
});

// `allDeliveries` gives current names; the decision itself only stored ids, so
// without it every card reads as a number.
export const decisionToRows = (decision, allDeliveries = []) => {
	if (!decision?.candidates?.length) {
		return [];
	}

	const driversById = Object.fromEntries(
		allDeliveries.map(d => [d.id, { name: `${d.firstname || ''} ${d.lastname || ''}`.trim() }]),
	);

	// The settings AS THEY WERE, in the shape skipReasonText reads. Without this
	// the board called it with no settings at all and every excluded rider read
	// «Έχει ήδη 0 ή περισσότερες ενεργές παραγγελίες» — a sentence that is true
	// of everybody and names a limit nobody set.
	//
	// The two city-column limits are not in the scoring blob; the sweep records
	// them under `context.limits`. A decision stored before that shipped has
	// none, and skipReasonText falls back to the un-numbered wording rather than
	// inventing a zero.
	const settings = {
		orderAutoAssignMinFreeOrders: decision.context?.limits?.minFreeOrders ?? null,
		orderAutoAssignMinFreeMinutes: decision.context?.limits?.minFreeSeconds ?? null,
		autoAssignScoring: resolveScoring(decision.scoring),
	};

	return rowsFromVerdicts({
		verdicts: decision.candidates.map(toVerdict),
		driversById,
		// Resolved, because a decision stored before a knob existed would
		// otherwise render that knob as undefined rather than as its default.
		scoring: settings.autoAssignScoring,
		skipReasonText: reason => skipReasonText(reason, { settings }),
	});
};

// The ORDER's side of the decision, which the board does not cover.
//
// The score board answers "why this rider". It cannot answer "why this order
// first, and by how much" — that is decided before any rider is scored, from the
// order's urgency against the others waiting. The sweep has been storing it all
// along (`context` in assignment_decisions) and this screen was dropping it on
// the floor.
export const orderContextFor = decision => {
	const ctx = decision?.context;
	if (!ctx) return [];

	const out = [];

	if (ctx.supply) {
		out.push({
			key: 'supply',
			label: 'Εκείνη τη στιγμή',
			value: `${ctx.supply.freeRiders} ελεύθεροι / ${ctx.supply.waitingOrders} σε αναμονή`,
		});
	}

	if (ctx.urgency !== null && ctx.urgency !== undefined) {
		out.push({
			key: 'urgency',
			label: 'Πόσο επείγει',
			value: `${Math.round(ctx.urgency)} στα 100`,
			// Without the weight the number is unreadable: 40 decides everything at
			// a weight of 100 and almost nothing at 5.
			note:
				ctx.urgencyWeight !== null && ctx.urgencyWeight !== undefined
					? `βαρύτητα αναμονής ${ctx.urgencyWeight}`
					: null,
		});
	}

	if (ctx.pairValue !== null && ctx.pairValue !== undefined) {
		out.push({
			key: 'pair',
			label: 'Αξία ζεύγους',
			value: `${Math.round(ctx.pairValue)}`,
			// This is what actually ordered the pass — the number every waiting
			// order was ranked by, not the rider score shown below.
			note: 'επείγον × βαρύτητα + βαθμός διανομέα — με αυτό μπήκε μπροστά',
		});
	}

	return out;
};

export const TRIGGER_TEXT = {
	sweep: 'ο περιοδικός έλεγχος',
	lookahead: 'πρόωρη προσθήκη σε διαδρομή',
	retry: 'επαναπροσπάθεια μετά από μη απάντηση',
	// Historical, from before the per-order triggers were removed.
	creation: 'κατά την καταχώρηση (παλιό)',
	ready: 'μόλις ετοιμάστηκε (παλιό)',
};
